import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatDisplayName, formatPronouns } from '@/lib/displayName';
import { badgeStyle } from '@/lib/badgeStyle';
import {
  Heart, ArrowLeft, Lock, Camera, Save, Check,
  Sparkles, ChevronDown, Upload, Trash2, Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import SignOutButton from '@/components/SignOutButton';

const NAVY = '#1D2D44';
const GOLD = '#C9A84C';
const INK = '#0F1A2A';     // darker navy for page bg
const PAPER = '#16243D';   // section card bg
const MUTED = '#8FA0BD';
const LINE = 'rgba(201,168,76,0.22)';
const TEXT = '#F5F1E6';

const Section = ({ title, kicker, children, testid }) => (
  <section
    data-testid={testid}
    className="rounded-2xl p-6 sm:p-8 mb-6"
    style={{ backgroundColor: PAPER, border: `1px solid ${LINE}` }}
  >
    {kicker && (
      <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
        {kicker}
      </p>
    )}
    <h2 className="font-serif text-2xl sm:text-3xl mb-5" style={{ color: '#F5F1E6' }}>
      {title}
    </h2>
    {children}
  </section>
);

const Chip = ({ active, onClick, children, testid, styleOverride }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="rounded-full px-4 py-2 text-sm transition-all duration-200 border"
    style={
      styleOverride || {
        borderColor: active ? GOLD : 'rgba(255,255,255,0.18)',
        backgroundColor: active ? GOLD : 'transparent',
        color: active ? NAVY : '#E8E2D2',
        fontWeight: active ? 600 : 400,
      }
    }
  >
    {children}
  </button>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
      {label}
    </label>
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testid}
        className="w-full appearance-none rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition"
        style={{
          backgroundColor: '#0F1A2A',
          color: '#F5F1E6',
          border: `1px solid ${LINE}`,
        }}
      >
        <option value="">— Select —</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <ChevronDown
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: GOLD }}
        size={16}
      />
    </div>
  </div>
);

/* Authenticated <img> using blob fetch — photos are private */
const AuthImage = ({ photoId, alt, axiosAuth }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    (async () => {
      try {
        const api = axiosAuth();
        const resp = await api.get(`/profile/photos/${photoId}`, { responseType: 'blob' });
        if (revoked) return;
        objectUrl = URL.createObjectURL(resp.data);
        setUrl(objectUrl);
      } catch (e) {
        console.error('photo fetch failed', e);
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId, axiosAuth]);

  if (!url) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: INK, color: MUTED }}
      >
        <Camera size={20} />
      </div>
    );
  }
  return <img src={url} alt={alt} className="w-full h-full object-cover" />;
};

const LockedView = ({ completed, total }) => (
  <main className="max-w-2xl mx-auto px-4 py-16 text-center">
    <div
      className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-8"
      style={{ backgroundColor: NAVY, border: `1px solid ${LINE}` }}
    >
      <Lock style={{ color: GOLD }} size={28} />
    </div>
    <p className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>
      Profile Locked
    </p>
    <h1 className="font-serif text-3xl sm:text-4xl mb-4" style={{ color: '#F5F1E6' }}>
      Your profile unlocks after the Map.
    </h1>
    <p className="leading-relaxed mb-8" style={{ color: MUTED }}>
      Profiles are introduced only after you complete all 6 checkpoints of The
      Relationship Map — so connections begin with clarity, not browsing.
    </p>
    <div
      className="inline-flex items-center gap-3 rounded-full px-5 py-2 mb-8"
      style={{ border: `1px solid ${LINE}`, color: '#E8E2D2' }}
      data-testid="profile-progress-indicator"
    >
      <span className="font-serif text-lg" style={{ color: GOLD }}>{completed}</span>
      <span className="text-sm">of {total} checkpoints completed</span>
    </div>
    <div>
      <Link to="/dashboard">
        <Button
          data-testid="back-to-map-btn"
          className="rounded-full px-6"
          style={{ backgroundColor: GOLD, color: NAVY }}
        >
          Continue Your Map
        </Button>
      </Link>
    </div>
  </main>
);

const ProfilePage = () => {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromReflections = location.state?.from === 'reflections';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [options, setOptions] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Section 2: Basics
  const [firstName, setFirstName] = useState('');
  const [lastNameInitial, setLastNameInitial] = useState('');
  const [age, setAge] = useState('');
  const [pronouns, setPronouns] = useState('Prefer not to say');
  const [city, setCity] = useState('');
  // Section 3: My Energy
  const [myEnergy, setMyEnergy] = useState('');
  // Section 4: Relationship Rhythm
  const [rhythm, setRhythm] = useState({});
  // Love Language reflections (shown when awareness === 'exploring')
  const [llReflection1, setLlReflection1] = useState('');
  const [llReflection2, setLlReflection2] = useState('');
  const [llReflection3, setLlReflection3] = useState('');
  const [llSuggestedPattern, setLlSuggestedPattern] = useState(null);
  // Section 5: How I Love
  const [howILove, setHowILove] = useState('');
  // Section 6: Intentionality Badges
  const [badges, setBadges] = useState([]);
  // Section 7: Guided Prompts (max 3)
  const [prompts, setPrompts] = useState([]);
  // Section 8: What I'm Looking For — strict filter layer
  const [relationshipIntent, setRelationshipIntent] = useState('');
  const [emotionalPacePref, setEmotionalPacePref] = useState('');
  const [valuesPriority, setValuesPriority] = useState([]);
  const [commRhythmPref, setCommRhythmPref] = useState('');

  const loadAll = useCallback(async () => {
    try {
      const api = axiosAuth();
      const accessRes = await api.get('/profile/access');
      setAccess(accessRes.data);

      if (!accessRes.data.unlocked) {
        setLoading(false);
        return;
      }

      const [optsRes, profileRes] = await Promise.all([
        api.get('/profile/options'),
        api.get('/profile'),
      ]);
      setOptions(optsRes.data);
      const p = profileRes.data || {};
      setPhotos(p.photos || []);
      setFirstName(p.first_name || '');
      setLastNameInitial(p.last_name_initial || '');
      setAge(p.age ? String(p.age) : '');
      setPronouns(p.pronouns || 'Prefer not to say');
      setCity(p.city || '');
      setMyEnergy(p.my_energy || '');
      setRhythm(p.relationship_rhythm || {});
      setLlReflection1(p.ll_reflection_1 || '');
      setLlReflection2(p.ll_reflection_2 || '');
      setLlReflection3(p.ll_reflection_3 || '');
      setLlSuggestedPattern(p.ll_suggested_pattern || null);
      setHowILove(p.how_i_love || '');
      setBadges(p.intentionality_badges || []);
      setPrompts(p.guided_prompts || []);
      setRelationshipIntent(p.relationship_intent || '');
      setEmotionalPacePref(p.emotional_pace_preference || '');
      setValuesPriority(p.values_priority || []);
      setCommRhythmPref(p.communication_rhythm_preference || '');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleBadge = (b) => {
    setBadges((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : prev.length >= 4 ? prev : [...prev, b]
    );
  };

  const togglePrompt = (label) => {
    const existing = prompts.find((p) => p.prompt === label);
    if (existing) {
      setPrompts(prompts.filter((p) => p.prompt !== label));
    } else if (prompts.length < 3) {
      setPrompts([...prompts, { prompt: label, answer: '' }]);
    } else {
      toast.info('Choose up to 3 prompts.');
    }
  };

  const updatePromptAnswer = (label, value) => {
    setPrompts(prompts.map((p) => (p.prompt === label ? { ...p, answer: value } : p)));
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (photos.length >= 6) {
      toast.error('You can upload up to 6 photos.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be under 10MB.');
      return;
    }
    setUploading(true);
    try {
      const api = axiosAuth();
      const form = new FormData();
      form.append('file', file);
      const resp = await api.post('/profile/photos', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotos((prev) => [...prev, {
        id: resp.data.id, order: resp.data.order, content_type: resp.data.content_type,
      }]);
      toast.success('Photo added.');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const api = axiosAuth();
      await api.delete(`/profile/photos/${photoId}`);
      setPhotos((prev) =>
        prev.filter((p) => p.id !== photoId).map((p, i) => ({ ...p, order: i }))
      );
      toast.success('Photo removed.');
    } catch (e) {
      console.error(e);
      toast.error('Could not remove photo.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      const payload = {
        first_name: firstName.trim() || null,
        last_name_initial: lastNameInitial.trim() === '' ? '' : lastNameInitial.trim(),
        age: age ? parseInt(age, 10) : null,
        pronouns: pronouns || 'Prefer not to say',
        city: city.trim() || null,
        my_energy: myEnergy.trim() || null,
        relationship_rhythm: Object.keys(rhythm).length ? rhythm : null,
        how_i_love: howILove.trim() || null,
        intentionality_badges: badges,
        guided_prompts: prompts.filter((p) => p.answer.trim()),
        ll_reflection_1: llReflection1.trim() === '' ? '' : llReflection1.trim(),
        ll_reflection_2: llReflection2.trim() === '' ? '' : llReflection2.trim(),
        ll_reflection_3: llReflection3.trim() === '' ? '' : llReflection3.trim(),
        relationship_intent: relationshipIntent || '',
        emotional_pace_preference: emotionalPacePref || '',
        values_priority: valuesPriority,
        communication_rhythm_preference: commRhythmPref || '',
      };
      await api.post('/profile', payload);
      // Refresh from server so we pick up the freshly-computed pattern
      const refreshed = await api.get('/profile');
      setLlSuggestedPattern(refreshed.data?.ll_suggested_pattern || null);
      toast.success('Profile saved — private by default.');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  // ===== Page chrome =====
  return (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur"
        style={{ backgroundColor: 'rgba(15,26,42,0.85)', borderBottom: `1px solid ${LINE}` }}
      >
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to={cameFromReflections ? '/reflections' : '/dashboard'}
            state={cameFromReflections ? { from: 'profile' } : undefined}
            data-testid="profile-back-link"
            className="flex items-center text-sm"
            style={{ color: MUTED }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{cameFromReflections ? 'Back to Reflections' : 'Back to Dashboard'}</span>
          </Link>
          <Link to="/dashboard" className="font-serif text-lg" style={{ color: '#F5F1E6' }} data-testid="brand-logo-link">
            Our Love <Heart className="inline-block w-4 h-4 mx-1" style={{ color: GOLD }} strokeWidth={1.5} fill={GOLD} /> Connection
          </Link>
          <div className="flex items-center justify-end gap-3 w-20 sm:w-auto">
            <Link
              to="/profile/settings"
              data-testid="profile-settings-link"
              aria-label="Account Settings"
              title="Account Settings"
              className="inline-flex items-center justify-center rounded-full p-2 transition-colors"
              style={{ color: GOLD }}
            >
              <Settings className="w-5 h-5" strokeWidth={1.75} />
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      {loading && (
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" style={{ color: MUTED }}>
          <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: GOLD, borderTopColor: 'transparent' }} />
          Loading your profile…
        </div>
      )}

      {!loading && access && !access.unlocked && (
        <LockedView completed={access.completed_count} total={access.total} />
      )}

      {!loading && access?.unlocked && (
        <main className="max-w-3xl mx-auto px-4 py-10" data-testid="profile-builder">
          {/* Title */}
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: GOLD }}>
              Your Private Profile
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl mb-3" style={{ color: '#F5F1E6' }}>
              Build the version of you<br />you actually want to meet someone as.
            </h1>
            <p className="max-w-xl mx-auto leading-relaxed text-sm sm:text-base" style={{ color: MUTED }}>
              Your profile is private by default. You&apos;ll choose if and when to share it.
            </p>
          </div>

          {/* 1. PHOTOS */}
          <Section title="Photos" kicker="Section 1 · up to 6" testid="section-photos">
            <p className="text-sm mb-5" style={{ color: MUTED }}>
              Add up to 6 photos. JPEG, PNG, or WebP — under 10MB each.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  data-testid={`photo-${photo.order}`}
                  className="relative aspect-square rounded-xl overflow-hidden group"
                  style={{ border: `1px solid ${LINE}`, backgroundColor: INK }}
                >
                  <AuthImage photoId={photo.id} alt={`Photo ${photo.order + 1}`} axiosAuth={axiosAuth} />
                  <button
                    type="button"
                    data-testid={`delete-photo-${photo.order}`}
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    style={{ backgroundColor: 'rgba(15,26,42,0.85)', color: '#F5F1E6' }}
                    title="Remove photo"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div
                    className="absolute bottom-2 left-2 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded"
                    style={{ backgroundColor: NAVY, color: GOLD }}
                  >
                    #{photo.order + 1}
                  </div>
                </div>
              ))}

              {photos.length < 6 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-photo-btn"
                  disabled={uploading}
                  className="aspect-square rounded-xl flex flex-col items-center justify-center gap-2 transition disabled:opacity-60"
                  style={{
                    border: `1px dashed ${GOLD}`,
                    backgroundColor: 'rgba(201,168,76,0.04)',
                    color: GOLD,
                  }}
                >
                  {uploading ? (
                    <div className="w-5 h-5 border-2 rounded-full animate-spin"
                      style={{ borderColor: GOLD, borderTopColor: 'transparent' }} />
                  ) : (
                    <Upload size={20} />
                  )}
                  <span className="text-xs uppercase tracking-widest">
                    {uploading ? 'Uploading…' : 'Add Photo'}
                  </span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleUpload}
              data-testid="photo-file-input"
              className="hidden"
            />
          </Section>

          {/* 2. BASICS */}
          <Section title="Basics" kicker="Section 2" testid="section-basics">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                  First Name
                </label>
                <Input
                  data-testid="input-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={50}
                  placeholder="What you go by"
                  className="rounded-xl border-0 focus-visible:ring-2"
                  style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                  Last Name Initial
                  <span className="ml-2 normal-case tracking-normal text-[10px]" style={{ color: MUTED, opacity: 0.7 }}>
                    optional
                  </span>
                </label>
                <Input
                  data-testid="input-last-initial"
                  value={lastNameInitial}
                  onChange={(e) => {
                    // Allow only letters + optional trailing period, max 2 chars, uppercase
                    const cleaned = e.target.value
                      .replace(/[^A-Za-z.]/g, '')
                      .toUpperCase()
                      .slice(0, 2);
                    // Period only allowed at the end after a letter
                    if (cleaned === '.' || cleaned.startsWith('.')) {
                      setLastNameInitial('');
                    } else {
                      setLastNameInitial(cleaned);
                    }
                  }}
                  maxLength={2}
                  placeholder="e.g. W."
                  className="rounded-xl border-0 focus-visible:ring-2"
                  style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
                />
                <p className="text-[10px] mt-1.5" style={{ color: MUTED, opacity: 0.7 }}>
                  We only store the initial — never your full last name.
                </p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                  Age
                </label>
                <Input
                  data-testid="input-age"
                  type="number"
                  min={19}
                  max={99}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="—"
                  className="rounded-xl border-0"
                  style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                  Pronouns
                  <span className="ml-2 normal-case tracking-normal text-[10px]" style={{ color: MUTED, opacity: 0.7 }}>
                    optional
                  </span>
                </label>
                <div className="relative">
                  <select
                    data-testid="input-pronouns"
                    value={pronouns}
                    onChange={(e) => setPronouns(e.target.value)}
                    className="w-full appearance-none rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition"
                    style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
                  >
                    {(options?.pronouns || ['He/Him', 'She/Her', 'They/Them', 'Prefer not to say']).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ color: GOLD }}
                    size={16}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                  City
                </label>
                <Input
                  data-testid="input-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                  placeholder="Where you're based"
                  className="rounded-xl border-0"
                  style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
                />
              </div>
            </div>
            {(firstName || lastNameInitial) && (
              <p className="text-xs mt-4" style={{ color: MUTED }} data-testid="display-name-preview">
                Profile shows as{' '}
                <span className="font-serif" style={{ color: GOLD }}>
                  {formatDisplayName(firstName, lastNameInitial)}
                </span>
                {formatPronouns(pronouns) && (
                  <span className="ml-1" style={{ color: MUTED }}>
                    {' '}{formatPronouns(pronouns)}
                  </span>
                )}
              </p>
            )}
          </Section>

          {/* 3. MY ENERGY */}
          <Section title="My Energy" kicker="Section 3 · the vibe you bring" testid="section-energy">
            <Textarea
              data-testid="input-energy"
              value={myEnergy}
              onChange={(e) => setMyEnergy(e.target.value)}
              maxLength={280}
              rows={4}
              placeholder="A few words that describe how you show up in a room — calm, curious, playful, grounded…"
              className="rounded-xl border-0"
              style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
            />
            <p className="text-xs mt-2 text-right" style={{ color: MUTED }}>
              {myEnergy.length}/280
            </p>
          </Section>

          {/* 4. RELATIONSHIP RHYTHM */}
          <Section title="Relationship Rhythm" kicker="Section 4 · how you move through love" testid="section-rhythm">
            <div className="grid sm:grid-cols-2 gap-5">
              {options && Object.entries(options.relationship_rhythm)
                .filter(([key]) => key !== 'love_language_awareness')
                .map(([key, opts]) => (
                <SelectField
                  key={key}
                  testid={`rhythm-${key}`}
                  label={key.replace(/_/g, ' ')}
                  value={rhythm[key]}
                  onChange={(v) => setRhythm((prev) => {
                    const next = { ...prev };
                    if (v) next[key] = v;
                    else delete next[key];
                    return next;
                  })}
                  options={opts}
                />
              ))}
            </div>

            {/* Love Language Awareness — dedicated sub-block with conditional reflection flow */}
            <div
              className="mt-6 pt-6"
              style={{ borderTop: `1px solid ${LINE}` }}
              data-testid="love-language-block"
            >
              <label className="block text-xs uppercase tracking-[0.18em] mb-2" style={{ color: MUTED }}>
                Love Language Awareness
              </label>
              <div className="relative max-w-md">
                <select
                  data-testid="rhythm-love_language_awareness"
                  value={rhythm.love_language_awareness || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRhythm((prev) => {
                      const next = { ...prev };
                      if (v) next.love_language_awareness = v;
                      else delete next.love_language_awareness;
                      return next;
                    });
                  }}
                  className="w-full appearance-none rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition"
                  style={{ backgroundColor: INK, color: TEXT, border: `1px solid ${LINE}` }}
                >
                  <option value="">— Select —</option>
                  <option value="known">I know my love language</option>
                  <option value="exploring">I&apos;m still discovering it</option>
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: GOLD }} size={16}
                />
              </div>

              {/* Reflection flow appears only when 'exploring' */}
              {rhythm.love_language_awareness === 'exploring' && (
                <div className="mt-6" data-testid="ll-reflection-flow">
                  <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: GOLD }}>
                    A quick reflection (optional)
                  </p>
                  <p className="text-xs mb-4" style={{ color: MUTED }}>
                    Clarity is discovered, not assumed. Answer any of these — or skip them.
                  </p>

                  {[
                    { id: 1, value: llReflection1, setter: setLlReflection1, label: 'I feel most appreciated when someone…' },
                    { id: 2, value: llReflection2, setter: setLlReflection2, label: 'I feel disconnected in a relationship when…' },
                    { id: 3, value: llReflection3, setter: setLlReflection3, label: 'I naturally show love by…' },
                  ].map((r) => (
                    <div key={r.id} className="mb-4">
                      <p className="font-serif italic text-sm mb-1" style={{ color: TEXT }}>
                        {r.label}
                      </p>
                      <Textarea
                        data-testid={`ll-reflection-${r.id}`}
                        value={r.value}
                        onChange={(e) => r.setter(e.target.value)}
                        maxLength={280}
                        rows={2}
                        placeholder="Your honest words — there's no wrong answer."
                        className="rounded-lg border-0"
                        style={{ backgroundColor: INK, color: TEXT, border: `1px solid ${LINE}` }}
                      />
                      <p className="text-xs mt-1 text-right" style={{ color: MUTED }}>
                        {r.value.length}/280
                      </p>
                    </div>
                  ))}

                  {llSuggestedPattern && (
                    <div
                      data-testid="ll-suggested-pattern-panel"
                      className="rounded-xl p-4 mt-4"
                      style={{
                        backgroundColor: 'rgba(94,127,99,0.10)',
                        border: '1px solid rgba(94,127,99,0.30)',
                      }}
                    >
                      <p className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: '#A3D9A5' }}>
                        Communication &amp; Emotional Style Insight
                      </p>
                      <p className="text-xs mb-2" style={{ color: MUTED }}>
                        Possible pattern (optional insight)
                      </p>
                      <p className="font-serif text-lg" style={{ color: TEXT }}>
                        {llSuggestedPattern}
                      </p>
                      <p className="text-[11px] mt-2 italic" style={{ color: MUTED }}>
                        A gentle observation — not a diagnosis. You can refine your answers anytime.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* 5. HOW I LOVE */}
          <Section title="How I Love" kicker="Section 5 · the way you give love" testid="section-how-i-love">
            <Textarea
              data-testid="input-how-i-love"
              value={howILove}
              onChange={(e) => setHowILove(e.target.value)}
              maxLength={280}
              rows={4}
              placeholder="When I care about someone, here's what shows up most…"
              className="rounded-xl border-0"
              style={{ backgroundColor: INK, color: '#F5F1E6', border: `1px solid ${LINE}` }}
            />
            <p className="text-xs mt-2 text-right" style={{ color: MUTED }}>
              {howILove.length}/280
            </p>
          </Section>

          {/* 6. INTENTIONALITY BADGES */}
          <Section title="Intentionality Badges" kicker="Section 6 · choose up to 4" testid="section-badges">
            <div className="flex flex-wrap gap-2">
              {options?.intentionality_badges.map((b) => (
                <Chip
                  key={b}
                  testid={`badge-${b.replace(/\s+/g, '-').toLowerCase()}`}
                  active={badges.includes(b)}
                  onClick={() => toggleBadge(b)}
                  styleOverride={badgeStyle(b, badges.includes(b))}
                >
                  {b}
                </Chip>
              ))}
            </div>
            <p className="text-xs mt-3" style={{ color: MUTED }}>
              {badges.length}/4 selected
            </p>
          </Section>

          {/* 7. GUIDED PROMPTS */}
          <Section title="Guided Prompts" kicker="Section 7 · pick 3" testid="section-prompts">
            <div className="flex flex-wrap gap-2 mb-5">
              {options?.guided_prompts.map((label) => {
                const active = prompts.some((p) => p.prompt === label);
                return (
                  <Chip
                    key={label}
                    testid={`prompt-toggle-${label.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                    active={active}
                    onClick={() => togglePrompt(label)}
                  >
                    {active ? <Check className="inline w-3 h-3 mr-1" /> : null}
                    {label}
                  </Chip>
                );
              })}
            </div>

            <div className="space-y-4">
              {prompts.map((p) => (
                <div
                  key={p.prompt}
                  data-testid={`prompt-card-${p.prompt.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                  className="rounded-xl p-4"
                  style={{ backgroundColor: INK, border: `1px solid ${LINE}` }}
                >
                  <div className="flex items-start gap-2 mb-3">
                    <Sparkles size={14} className="mt-1 flex-shrink-0" style={{ color: GOLD }} />
                    <p className="font-serif italic text-base" style={{ color: '#F5F1E6' }}>
                      {p.prompt}
                    </p>
                  </div>
                  <Textarea
                    value={p.answer}
                    onChange={(e) => updatePromptAnswer(p.prompt, e.target.value)}
                    maxLength={280}
                    rows={2}
                    placeholder="Your honest answer…"
                    className="rounded-lg border-0"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      color: '#F5F1E6',
                      border: `1px solid ${LINE}`,
                    }}
                  />
                  <p className="text-xs mt-1 text-right" style={{ color: MUTED }}>
                    {p.answer.length}/280
                  </p>
                </div>
              ))}
            </div>
          </Section>

          {/* 8. WHAT I'M LOOKING FOR — strict external filter layer */}
          <Section
            title="What I'm Looking For"
            kicker="Section 8 · partner-preference filter"
            testid="section-what-looking-for"
          >
            <p className="text-sm mb-6 italic" style={{ color: MUTED }}>
              This section is a preference filter, not a reflection. It describes what must be true
              of a partner for compatibility — it does not describe you.
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              <SelectField
                testid="wlf-relationship-intent"
                label="Relationship Intent"
                value={relationshipIntent}
                onChange={(v) => setRelationshipIntent(v || '')}
                options={options?.what_looking_for?.relationship_intent || []}
              />
              <SelectField
                testid="wlf-emotional-pace"
                label="Emotional Pace Preference"
                value={emotionalPacePref}
                onChange={(v) => setEmotionalPacePref(v || '')}
                options={options?.what_looking_for?.emotional_pace_preference || []}
              />
              <div className="sm:col-span-2">
                <SelectField
                  testid="wlf-comm-rhythm"
                  label="Communication Rhythm Preference"
                  value={commRhythmPref}
                  onChange={(v) => setCommRhythmPref(v || '')}
                  options={options?.what_looking_for?.communication_rhythm_preference || []}
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-xs uppercase tracking-[0.18em] mb-3" style={{ color: MUTED }}>
                Value Priorities
                <span className="ml-2 normal-case tracking-normal text-[10px]" style={{ color: MUTED, opacity: 0.7 }}>
                  pick up to {options?.max_value_priorities || 3}
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {(options?.what_looking_for?.values_priority || []).map((v) => {
                  const active = valuesPriority.includes(v);
                  return (
                    <Chip
                      key={v}
                      testid={`wlf-value-${v.replace(/\s+/g, '-').toLowerCase()}`}
                      active={active}
                      onClick={() => {
                        if (active) {
                          setValuesPriority(valuesPriority.filter((x) => x !== v));
                        } else if (valuesPriority.length >= (options?.max_value_priorities || 3)) {
                          toast.info(`Pick up to ${options?.max_value_priorities || 3} value priorities.`);
                        } else {
                          setValuesPriority([...valuesPriority, v]);
                        }
                      }}
                    >
                      {v}
                    </Chip>
                  );
                })}
              </div>
              <p className="text-xs mt-3" style={{ color: MUTED }}>
                {valuesPriority.length}/{options?.max_value_priorities || 3} selected
              </p>
            </div>
          </Section>

          {/* Save bar */}
          <div
            className="sticky bottom-0 -mx-4 px-4 py-4 mt-4 backdrop-blur"
            style={{ backgroundColor: 'rgba(15,26,42,0.92)', borderTop: `1px solid ${LINE}` }}
          >
            <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs" style={{ color: MUTED }}>
                <Lock size={12} />
                Private by default
              </div>
              <Button
                data-testid="save-profile-btn"
                onClick={handleSave}
                disabled={saving}
                className="rounded-full px-6 py-5 transition disabled:opacity-60"
                style={{ backgroundColor: GOLD, color: NAVY, fontWeight: 600 }}
              >
                {saving ? 'Saving…' : (<><Save className="w-4 h-4 mr-2" /> Save Profile</>)}
              </Button>
            </div>
          </div>
        </main>
      )}
    </div>
  );
};

export default ProfilePage;
